import { apiFetch } from './client';
import type { BeAuction } from './auctions';

export type StatusArrematado = 'OCUPADO' | 'DESOCUPADO' | 'EM_REFORMA' | 'A_VENDA' | 'VENDIDO';
export type CategoriaCusto =
  | 'ITBI'
  | 'REGISTRO'
  | 'REFORMA'
  | 'CONDOMINIO'
  | 'IPTU'
  | 'ADVOCACIA'
  | 'DESOCUPACAO'
  | 'LEILOEIRO'
  | 'FINANCIAMENTO'
  | 'OUTROS';

export interface BeCusto {
  id: string;
  arrematacaoId: string;
  categoria: CategoriaCusto;
  /** Quando o BE inclui a relação Prisma `category` no spread do custo. */
  category?: { code?: string } | null;
  descricao: string;
  valor: number;
  data: string;
  createdAt: string;
}

export interface BeVenda {
  id: string;
  arrematacaoId: string;
  valorVenda: number;
  comissao: number;
  dataVenda?: string;
}

export interface BeArrematacao {
  id: string;
  userId: string;
  auctionId: string;
  valorArrematado: number;
  dataArrematacao: string;
  status: StatusArrematado;
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
  auction: BeAuction;
  custos: BeCusto[];
  venda?: BeVenda;
}

export interface RoiResult {
  totalInvestido: number;
  totalCustos: number;
  valorVenda?: number;
  comissaoValor?: number;
  lucroLiquido: number | null;
  roi: number | null;
}

/** Rotas `api/v1/acquisitions` (Prisma: `Auction` / tabela `auctions`). */
export const acquisitionsApi = {
  list: () => apiFetch<BeArrematacao[]>('/acquisitions'),

  getById: (id: string) => apiFetch<BeArrematacao>(`/acquisitions/${id}`),

  create: (data: {
    opportunityId: string;
    valorArrematado: number;
    dataArrematacao: string;
    status?: StatusArrematado;
    observacoes?: string;
  }) =>
    apiFetch<BeArrematacao>('/acquisitions', {
      method: 'POST',
      body: {
        opportunityId: data.opportunityId,
        winningBidAmount: data.valorArrematado,
        acquiredAt: data.dataArrematacao,
        status: data.status,
        notes: data.observacoes,
      },
    }),

  update: (
    id: string,
    data: Partial<{ status: StatusArrematado; observacoes: string; valorArrematado: number }>
  ) => {
    const body: Record<string, unknown> = {};
    if (data.status !== undefined) body.status = data.status;
    if (data.observacoes !== undefined) body.notes = data.observacoes;
    if (data.valorArrematado !== undefined) body.winningBidAmount = data.valorArrematado;
    return apiFetch<BeArrematacao>(`/acquisitions/${id}`, { method: 'PATCH', body });
  },

  remove: (id: string) => apiFetch<void>(`/acquisitions/${id}`, { method: 'DELETE' }),

  addCusto: (
    id: string,
    data: { categoria: CategoriaCusto; descricao: string; valor: number; data?: string }
  ) =>
    apiFetch<BeCusto>(`/acquisitions/${id}/costs`, {
      method: 'POST',
      body: {
        categoria: data.categoria,
        description: data.descricao,
        valor: data.valor,
        data: data.data,
      },
    }),

  removeCusto: (id: string, costId: string) =>
    apiFetch<void>(`/acquisitions/${id}/costs/${costId}`, { method: 'DELETE' }),

  upsertVenda: (id: string, data: { valorVenda: number; comissao?: number; dataVenda?: string }) =>
    apiFetch<BeVenda>(`/acquisitions/${id}/sale`, { method: 'POST', body: data }),

  calcRoi: (id: string) => apiFetch<RoiResult>(`/acquisitions/${id}/roi`),
};

/** Alias legado — prefira `acquisitionsApi`. */
export const arrematacoesApi = acquisitionsApi;
